import * as React from 'react';
import { Loader2 } from 'lucide-react';

import { cn } from '../../utils/cn';
import { prefersReducedMotion } from '../../utils/prefers-reduced-motion';
import { QUESTIONNAIRE_COLOUR } from './questionnaire-tokens';

export interface SubmittingScreenProps {
    heading: string;
    /** Label on the pill, e.g. `Sending…`. */
    actionLabel?: string;
    className?: string;
}

/**
 * The hold between the last answer and the confirmation.
 *
 * The bar is indeterminate on purpose: the request has no measurable progress,
 * and a bar that pretends otherwise stalls at 90% and reads as a failure. It
 * stops travelling under reduced motion, where the spinner and the live region
 * still carry the state.
 */
export const SubmittingScreen = ({
    heading,
    actionLabel = 'Sending…',
    className,
}: SubmittingScreenProps) => {
    const still = prefersReducedMotion();

    return (
        <div
            aria-live="polite"
            className={cn(
                'flex size-full flex-col items-center justify-center gap-6',
                className,
            )}
            role="status"
        >
            <p
                className={cn(
                    'font-sans text-7.5 leading-[1.2] font-medium tracking-[-0.03em]',
                    QUESTIONNAIRE_COLOUR.inkHeading,
                )}
            >
                {heading}
            </p>

            <div className="h-0.5 w-56.5 overflow-hidden rounded-full bg-[#eaeaea]">
                <div
                    className={cn(
                        'h-full w-1/3 rounded-full bg-[#181818]',
                        !still && 'animate-questionnaire-progress',
                    )}
                />
            </div>

            <span
                className={cn(
                    'inline-flex h-10 items-center gap-2 rounded-[var(--radius-btn-m)] px-4.5 font-sans text-3.75 text-white',
                    QUESTIONNAIRE_COLOUR.actionPrimary,
                )}
            >
                {actionLabel}
                <Loader2
                    aria-hidden="true"
                    className={cn('size-5', !still && 'animate-spin')}
                />
            </span>
        </div>
    );
};
